// health.ts - typed wrapper around the /api/v1/health/* endpoints.
//
// In P1 only checkReady is needed. Subsequent phases will add live(),
// which is rarely called from the client (live is for process probes),
// but is here for completeness.

export interface ReadyResponse {
  status: 'ok';
}

export interface ErrorBody {
  code: string;
  message: string;
  requestId: string;
  details?: unknown;
}

export interface ErrorResponse {
  error: ErrorBody;
}

export class HealthCheckError extends Error {
  constructor(
    public readonly code: string,
    public readonly status: number,
    public readonly requestId: string,
    message: string,
  ) {
    super(message);
    this.name = 'HealthCheckError';
  }
}

// checkReady calls GET /api/v1/health/ready and throws HealthCheckError
// when the server returns a non-2xx response. Network errors propagate
// as the original TypeError from fetch.
export async function checkReady(signal?: AbortSignal): Promise<ReadyResponse> {
  const resp = await fetch('/api/v1/health/ready', { signal });
  if (!resp.ok) {
    let body: ErrorResponse | undefined;
    try {
      body = (await resp.json()) as ErrorResponse;
    } catch {
      // Server returned non-JSON; fall through with undefined body.
    }
    throw new HealthCheckError(
      body?.error?.code ?? 'UNKNOWN',
      resp.status,
      body?.error?.requestId ?? '',
      body?.error?.message ?? `health check failed: HTTP ${resp.status}`,
    );
  }
  return (await resp.json()) as ReadyResponse;
}
